// 역할: 전체 애플리케이션 레이아웃 관리 (헤더 + 사이드바 + 카테고리 + 콘텐츠)
// React 및 타입 import
import type { ReactNode } from 'react';

import { useState, useEffect, useCallback } from 'react';

// hooks
import { useDeviceDetect } from '../../hooks/useDeviceDetect';
import { useLocalizedRouting } from '../../hooks/useLocalizedRouting';
import { useSeoSync } from '../../hooks/useSeoSync';

// components
import { CategoryArea } from './CategoryArea';
import { ContentArea } from './ContentArea';
import { HeaderArea } from './HeaderArea';
import { SidebarArea } from './SidebarArea';

interface MainLayoutProps {
  children?: ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { isMobile, isDesktop } = useDeviceDetect();

  // 리전별 언어 자동 전환 활성화
  useLocalizedRouting();

  // 라우트 변경 시 SEO meta 태그 동기화 (server.js 주입 메타를 갱신)
  useSeoSync();

  // ✅ P0-3: Improved body scroll lock with proper cleanup
  useEffect(() => {
    // Store original overflow value
    const originalOverflow = document.body.style.overflow;

    if (isMobile && isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Always restore original overflow on cleanup
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobile, isMobileSidebarOpen]);

  // ✅ P1-2: Consolidated mobile sidebar effects
  useEffect(() => {
    // ESC key handler for mobile sidebar
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };

    if (isMobile) {
      // Register escape key listener
      document.addEventListener('keydown', handleEscape);

      // Ensure desktop sidebar is open when switching to mobile
      setIsSidebarOpen(true);

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    } else if (isMobileSidebarOpen) {
      // Close mobile sidebar when switching to desktop
      setIsMobileSidebarOpen(false);
    }
  }, [isMobile, isMobileSidebarOpen]);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    } else {
      setIsSidebarOpen(!isSidebarOpen);
    }
  }, [isMobile, isSidebarOpen, isMobileSidebarOpen]);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
  }, []);

  return (
    <div className='flex h-screen min-h-screen flex-col'>
      {/* 헤더 영역 */}
      <HeaderArea hideBottomBorder={isMobile && isMobileSidebarOpen} />

      {/* 메인 콘텐츠 영역 */}
      <div className='flex flex-1 relative pt-[48px] xl:pt-[104px] min-h-0 overflow-hidden'>
        {/* 모바일 오버레이 배경 */}
        {isMobile && isMobileSidebarOpen && (
          <div
            className='fixed inset-0 bg-black bg-opacity-60 z-40 transition-opacity duration-300'
            onClick={closeMobileSidebar}
            aria-hidden='true'
          />
        )}

        {/* 사이드바 영역 */}
        <SidebarArea
          isOpen={isMobile ? isMobileSidebarOpen : isSidebarOpen}
          onToggle={toggleSidebar}
          onClose={closeMobileSidebar}
          isMobile={isMobile}
        />

        {/* 콘텐츠 영역 */}
        <div className='flex-1 flex flex-col min-h-0'>
          {/* 카테고리 영역 */}
          <CategoryArea isSidebarOpen={isDesktop ? isSidebarOpen : false} onToggleSidebar={toggleSidebar} />

          {/* 실제 콘텐츠 영역 */}
          <ContentArea>
            <div className='flex-1 min-h-0'>{children}</div>
          </ContentArea>
        </div>
      </div>
    </div>
  );
};
